import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Archive, ArchiveRestore, Trash2, Loader2, ArrowUpDown, KeyRound, Settings2 } from "lucide-react";
import { TenantSagaVariablesTab } from "../components/tenant/TenantSagaVariablesTab";
import { TenantLibraryVariablesTab } from "../components/tenant/TenantLibraryVariablesTab";
import {
  archiveTenant,
  deleteTenantPermanently,
  getTenant,
  getTenantAuditLogs,
  getTenantSagas,
  migrateTenantTier,
  restoreTenant,
  rotateTenantSecrets,
} from "../services/tenant";
import type { Tenant } from "../types/tenant";
import type { AuditLog } from "../types/audit";
import type { SagaRunResult } from "../types/saga";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/saga/StatusPill";
import { RequireRole } from "../components/RequireRole";
import { cn } from "../utils/cn";

const TIER_CLS: Record<string, string> = {
  free: "text-muted-foreground",
  starter: "text-primary-soft-fg",
  pro: "text-warning-soft-fg",
  enterprise: "text-danger-soft-fg",
};

function MetadataRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-2 text-xs border-b border-border-subtle last:border-b-0">
      <span className="text-muted-foreground uppercase tracking-wider text-[10px] font-medium pt-0.5">{label}</span>
      <div className="font-mono-tight text-foreground/90 break-all">{value}</div>
    </div>
  );
}

export default function TenantDetail() {
  const { tenantKey } = useParams<{ tenantKey: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [sagas, setSagas] = useState<SagaRunResult[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (!tenantKey) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [t, s, a] = await Promise.all([
          getTenant(tenantKey),
          getTenantSagas(tenantKey, { limit: 50 }),
          getTenantAuditLogs(tenantKey),
        ]);
        if (!alive) return;
        setTenant(t);
        setSagas(s.data);
        setAuditLogs(a);
        setErr(null);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Erro ao carregar tenant");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenantKey]);

  async function handleRotateSecrets() {
    if (!tenant) return;
    if (!window.confirm(
      `Rotacionar secrets do tenant '${tenant.tenantKey}'?\n\n` +
      "IDP client secret, DB password e IAM access key serão regenerados. " +
      "Apps em runtime vão precisar refazer handshake. Operação rápida (~ms).",
    )) return;
    setActionPending(true);
    try {
      const r = await rotateTenantSecrets(tenant.tenantKey);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao rotacionar secrets");
      setActionPending(false);
    }
  }

  async function handleMigrateTier() {
    if (!tenant) return;
    const targetTier = window.prompt(
      `Migrar tenant '${tenant.tenantKey}' para qual tier?\n\nOpções: free, starter, pro, enterprise\nAtual: ${tenant.billingTier}`,
    );
    if (targetTier === null) return;
    const trimmed = targetTier.trim().toLowerCase();
    if (!["free", "starter", "pro", "enterprise"].includes(trimmed)) {
      setErr(`Tier inválido: '${trimmed}'. Aceitos: free, starter, pro, enterprise`);
      return;
    }
    if (trimmed === tenant.billingTier) {
      setErr(`Tenant já está em '${trimmed}'.`);
      return;
    }
    setActionPending(true);
    try {
      const r = await migrateTenantTier(tenant.tenantKey, trimmed);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao migrar tier");
      setActionPending(false);
    }
  }

  async function handleArchive() {
    if (!tenant) return;
    if (!window.confirm(`Arquivar tenant '${tenant.tenantKey}'?\n\nTodos os recursos serão suspensos. Você poderá restaurar depois.`)) return;
    setActionPending(true);
    try {
      const r = await archiveTenant(tenant.tenantKey);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao arquivar");
      setActionPending(false);
    }
  }

  async function handleRestore() {
    if (!tenant) return;
    if (!window.confirm(`Restaurar tenant '${tenant.tenantKey}'?\n\nTodos os recursos serão reativados.`)) return;
    setActionPending(true);
    try {
      const r = await restoreTenant(tenant.tenantKey);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao restaurar");
      setActionPending(false);
    }
  }

  async function handleDelete() {
    if (!tenant) return;
    const typed = window.prompt(
      `⚠ EXCLUSÃO DEFINITIVA E IRREVERSÍVEL\n\nIsso vai destruir realm IDP, database, bucket S3, DNS, branding e templates do tenant '${tenant.tenantKey}'.\n\nDigite a tenant key para confirmar:`,
    );
    if (typed === null) return;
    if (typed.trim() !== tenant.tenantKey) {
      setErr(`Confirmação não bateu (digite exatamente '${tenant.tenantKey}'). Cancelado.`);
      return;
    }
    setActionPending(true);
    try {
      const r = await deleteTenantPermanently(tenant.tenantKey);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao excluir");
      setActionPending(false);
    }
  }

  if (!tenantKey) {
    return (
      <div className="p-6">
        <p className="text-danger text-sm">tenantKey inválido.</p>
        <Link to="/tenants" className="inline-block mt-3"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Tenants</Button></Link>
      </div>
    );
  }

  if (loading && !tenant) {
    return (
      <>
        <PageHeader title="Carregando…" actions={<Link to="/tenants"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Tenants</Button></Link>} />
        <div className="p-6 text-sm text-muted-foreground">Carregando tenant…</div>
      </>
    );
  }

  if (err && !tenant) {
    return (
      <>
        <PageHeader title="Tenant não encontrado" actions={<Link to="/tenants"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Tenants</Button></Link>} />
        <div className="p-6">
          <div className="rounded-md border border-danger/20 bg-danger-soft p-4 text-sm text-danger-soft-fg">{err}</div>
        </div>
      </>
    );
  }

  if (!tenant) return null;

  const meta = tenant.metadata ?? {};
  const realmId = meta.realmId as string | undefined;
  const realmUrl = meta.realmUrl as string | undefined;
  const dbName = meta.dbName as string | undefined;
  const subdomain = meta.subdomain as string | undefined;
  const bucketName = meta.bucketName as string | undefined;
  const assetsUrl = meta.assetsUrl as string | undefined;
  const templateIds = meta.templateIds as string[] | undefined;
  const clientId = meta.clientId as string | undefined;

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <code className="font-mono-tight">{tenant.tenantKey}</code>
            <StatusPill status={tenant.status} />
          </span>
        }
        subtitle={
          <span className="text-muted-foreground">
            {tenant.name}
            {" · "}
            <span className={cn("font-mono-tight uppercase tracking-wider text-[11px]", TIER_CLS[tenant.billingTier] ?? "text-muted-foreground")}>{tenant.billingTier}</span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/tenants"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Tenants</Button></Link>
            <RequireRole anyOf={["platform-master", "platform-admin", "platform-operations"]}>
              {tenant.status === "ACTIVE" && (
                <>
                  <Button size="sm" variant="outline" onClick={handleRotateSecrets} disabled={actionPending} title="Rotacionar IDP secret + DB password + IAM access key">
                    <KeyRound size={14} />
                    Rotacionar secrets
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleMigrateTier} disabled={actionPending} title="Mudar billing tier">
                    <ArrowUpDown size={14} />
                    Mudar tier
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleArchive} disabled={actionPending}>
                    {actionPending ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
                    Arquivar
                  </Button>
                </>
              )}
              {tenant.status === "ARCHIVED" && (
                <>
                  <Button size="sm" variant="outline" onClick={handleRestore} disabled={actionPending}>
                    {actionPending ? <Loader2 size={14} className="animate-spin" /> : <ArchiveRestore size={14} />}
                    Restaurar
                  </Button>
                  <RequireRole anyOf={["platform-admin"]}>
                    <Button size="sm" variant="danger" onClick={handleDelete} disabled={actionPending}>
                      <Trash2 size={14} />
                      Excluir definitivo
                    </Button>
                  </RequireRole>
                </>
              )}
            </RequireRole>
          </div>
        }
      />

      <div className="p-6 space-y-5">
        {err && tenant && (
          <div className="rounded-md border border-danger/20 bg-danger-soft p-3 text-xs text-danger-soft-fg">{err}</div>
        )}

        {/* Identidade + Metadata grid 2-col */}
        <div className="grid lg:grid-cols-[1fr_2fr] gap-5">
          {/* Identidade */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4">Identidade</h2>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Branding</p>
                {tenant.brandingColor ? (
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className="w-8 h-8 rounded-md border border-border-strong shrink-0"
                      style={{ backgroundColor: tenant.brandingColor }}
                      aria-hidden="true"
                    />
                    <code className="font-mono-tight text-sm">{tenant.brandingColor}</code>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">—</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Criado em</p>
                <p className="font-mono-tight text-xs mt-0.5">{new Date(tenant.createdAt).toLocaleString("pt-BR")}</p>
              </div>
              {tenant.archivedAt && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Arquivado em</p>
                  <p className="font-mono-tight text-xs mt-0.5">{new Date(tenant.archivedAt).toLocaleString("pt-BR")}</p>
                </div>
              )}
            </div>
          </section>

          {/* Metadata expandida */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Metadata downstream</h2>
            <div>
              <MetadataRow label="realmId" value={realmId ? <code>{realmId}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="realmUrl" value={realmUrl ? <a href={realmUrl} target="_blank" rel="noreferrer" className="text-primary-soft-fg hover:underline">{realmUrl}</a> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="dbName" value={dbName ? <code>{dbName}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="subdomain" value={subdomain ? <code>{subdomain}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="bucketName" value={bucketName ? <code>{bucketName}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="assetsUrl" value={assetsUrl ? <code>{assetsUrl}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="clientId" value={clientId ? <code>{clientId}</code> : <span className="text-muted-foreground">—</span>} />
              <MetadataRow label="templateIds" value={templateIds && templateIds.length > 0 ? <span className="text-xs">{templateIds.join(", ")}</span> : <span className="text-muted-foreground">—</span>} />
            </div>
          </section>
        </div>

        {/* Variáveis de Saga (SPEC-20260507-2200 + 2300) */}
        <section className="rounded-md border border-border bg-surface overflow-hidden">
          <header className="flex items-center gap-2 px-5 py-3 border-b border-border-subtle">
            <Settings2 size={14} className="text-muted-foreground" />
            <h2 className="text-sm font-medium">Variáveis de Saga</h2>
            <span className="text-xs text-muted-foreground">
              valores pré-configurados que o orchestrator usa em runtime
            </span>
          </header>
          <div className="px-5 py-4">
            {tenantKey && <TenantVariablesTabbed tenantKey={tenantKey} />}
          </div>
        </section>

        {/* Saga history */}
        <section className="rounded-md border border-border bg-surface overflow-hidden">
          <header className="flex items-center justify-between px-5 py-3 border-b border-border-subtle">
            <h2 className="text-sm font-medium">Saga history</h2>
            <span className="text-xs text-muted-foreground">{sagas.length} {sagas.length === 1 ? "saga" : "sagas"}</span>
          </header>
          {sagas.length === 0 ? (
            <p className="px-5 py-6 text-xs text-muted-foreground text-center">Nenhuma saga relacionada a este tenant.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border-subtle">
                  <th className="text-left font-medium px-5 py-2 w-32">Saga ID</th>
                  <th className="text-left font-medium px-5 py-2">Tipo</th>
                  <th className="text-left font-medium px-5 py-2 w-32">Status</th>
                  <th className="text-left font-medium px-5 py-2 w-20">Steps</th>
                  <th className="text-left font-medium px-5 py-2 w-44">Iniciada</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {sagas.map((s) => (
                  <tr key={s.sagaId} className="hover:bg-surface-2 transition-colors">
                    <td className="px-5 py-2.5">
                      <Link to={`/sagas/${s.sagaId}`} className="font-mono-tight text-xs text-foreground hover:underline">
                        {s.sagaId.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-5 py-2.5 font-medium">{s.sagaType}</td>
                    <td className="px-5 py-2.5"><StatusPill status={s.status} /></td>
                    <td className="px-5 py-2.5 font-mono-tight text-xs text-muted-foreground">{s.currentStep}/{s.totalSteps}</td>
                    <td className="px-5 py-2.5 font-mono-tight text-xs text-muted-foreground">
                      {new Date(s.startedAt).toLocaleString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Audit log */}
        <section className="rounded-md border border-border bg-surface overflow-hidden">
          <header className="flex items-center justify-between px-5 py-3 border-b border-border-subtle">
            <h2 className="text-sm font-medium">Audit log</h2>
            <span className="text-xs text-muted-foreground">{auditLogs.length} {auditLogs.length === 1 ? "evento" : "eventos"}</span>
          </header>
          {auditLogs.length === 0 ? (
            <p className="px-5 py-6 text-xs text-muted-foreground text-center">Sem eventos auditados para este tenant.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border-subtle">
                  <th className="text-left font-medium px-5 py-2">Ação</th>
                  <th className="text-left font-medium px-5 py-2 w-48">Operador</th>
                  <th className="text-left font-medium px-5 py-2 w-28">Status</th>
                  <th className="text-left font-medium px-5 py-2 w-32">Saga</th>
                  <th className="text-left font-medium px-5 py-2 w-44">Quando</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-surface-2 transition-colors">
                    <td className="px-5 py-2.5"><code className="font-mono-tight text-xs">{log.action}</code></td>
                    <td className="px-5 py-2.5 text-xs">
                      <div className="font-medium">{log.actorUsername}</div>
                      <div className="text-[10px] text-muted-foreground font-mono-tight">{log.actorRoles.join(", ")}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium font-mono-tight",
                        log.status === "SUCCESS" && "bg-success-soft text-success-soft-fg border-success/20",
                        log.status === "FAILED" && "bg-danger-soft text-danger-soft-fg border-danger/20",
                        log.status === "PENDING" && "bg-surface-2 text-muted-foreground border-border",
                      )}>
                        <span className={cn(
                          "w-1.5 h-1.5 rounded-full",
                          log.status === "SUCCESS" && "bg-success",
                          log.status === "FAILED" && "bg-danger",
                          log.status === "PENDING" && "bg-muted-foreground",
                        )} aria-hidden="true" />
                        {log.status}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      {log.sagaId ? (
                        <Link to={`/sagas/${log.sagaId}`} className="font-mono-tight text-xs hover:underline">{log.sagaId.slice(0, 8)}</Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 font-mono-tight text-xs text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * SPEC-20260507-2300: tabs pra os 2 escopos cuja UI faz sentido neste pane:
 * - "Library" (X, null, null) — variáveis valendo pra todas as sagas
 * - "Por saga" (X, Y, null) — config tradicional per-saga (SPEC-2200)
 *
 * Escopo (X, Y, Z) por step fica no editor da saga_definition (placeholder.value)
 * — não aqui, pra evitar UX explosiva (N sagas × M steps).
 */
function TenantVariablesTabbed({ tenantKey }: { tenantKey: string }) {
  const [tab, setTab] = useState<"library" | "by-saga">("by-saga");
  return (
    <div>
      <div className="flex border-b border-border-subtle mb-3 -mt-1">
        <button
          type="button"
          onClick={() => setTab("by-saga")}
          className={cn(
            "px-3 py-2 text-xs font-medium border-b-2 transition-colors",
            tab === "by-saga"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          Por saga
        </button>
        <button
          type="button"
          onClick={() => setTab("library")}
          className={cn(
            "px-3 py-2 text-xs font-medium border-b-2 transition-colors",
            tab === "library"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          Library (todas as sagas)
        </button>
      </div>
      {tab === "by-saga" && <TenantSagaVariablesTab tenantKey={tenantKey} />}
      {tab === "library" && <TenantLibraryVariablesTab tenantKey={tenantKey} />}
    </div>
  );
}
